import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Alert, KeenIcon } from '@/components';
import { useAuthContext } from '@/auth';
import { useState } from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useLayout } from '@/providers';
import FormInput from '@/components/ui/input/FormInput';
import { FormattedMessage } from 'react-intl';
import { useIntl } from 'react-intl';

const ResetPasswordChange = () => {
  const intl = useIntl();
  const passwordSchema = Yup.object().shape({
    newPassword: Yup.string()
      .min(6, intl.formatMessage({ id: 'AUTH.RESET_PASS.VALIDATION.MIN_LENGTH' }))
      .required(intl.formatMessage({ id: 'AUTH.RESET_PASS.VALIDATION.REQUIRED' })),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], intl.formatMessage({ id: 'AUTH.RESET_PASS.VALIDATION.MATCH' }))
      .required(intl.formatMessage({ id: 'AUTH.RESET_PASS.VALIDATION.CONFIRM_REQUIRED' }))
  });

  const {
    currentLayout
  } = useLayout();
  const {
    changePassword
  } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [hasErrors, setHasErrors] = useState(undefined);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordConfirmation, setShowNewPasswordConfirmation] = useState(false);

  const formik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema: passwordSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      setHasErrors(undefined);
      
      const success = await changePassword(values.newPassword, values.confirmPassword);
      
      if (success) {
        navigate(currentLayout?.name === 'auth-branded' ? '/auth/reset-password/changed' : '/auth/classic/reset-password/changed');
      } else {
        setHasErrors(true);
      }
      
      setLoading(false);
      setSubmitting(false);
    }
  });

  return <div className="card max-w-[370px] w-full">
      <form className="card-body flex flex-col gap-5 p-10" onSubmit={formik.handleSubmit} noValidate>
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900"><FormattedMessage id="AUTH.RESET_PASS.TITLE" /></h3>
          <span className="text-2sm text-gray-700"><FormattedMessage id="AUTH.RESET_PASS.PLACEHOLDER" /></span>
        </div>

        {hasErrors && <Alert variant="danger"><FormattedMessage id="AUTH.RESET_PASS.ERROR.FAILED" /></Alert>}

        <div className="flex flex-col gap-1">
          <FormInput
            type="password"
            label={<FormattedMessage id="AUTH.RESET_PASS.NEW_PASSWORD" />}
            name="newPassword"
            icon= {<KeenIcon icon="lock-2" className="text-lg" />}
            placeholder={intl.formatMessage({ id: 'AUTH.RESET_PASS.PLACEHOLDER' })}
            autoComplete="off"
            error={formik.errors.newPassword}
            touched={formik.touched.newPassword}
            {...formik.getFieldProps('newPassword')}
          />
        </div>

        <div className="flex flex-col gap-1">
          <FormInput
            type="password"
            label={<FormattedMessage id="AUTH.RESET_PASS.CONFIRM_PASSWORD" />}
            name="confirmPassword"
            icon= {<KeenIcon icon="key" className="text-lg" />}
            placeholder={intl.formatMessage({id: 'AUTH.RESET_PASS.CONFIRM_PASSWORD_PLACEHOLDER'})}
            autoComplete="off"
            error={formik.errors.confirmPassword}
            touched={formik.touched.confirmPassword}
            {...formik.getFieldProps('confirmPassword')}
          />
        </div>

        <button type="submit" className="btn btn-primary flex justify-center grow" disabled={loading}>
          {loading ? <FormattedMessage id="AUTH.PLEASE_WAIT" /> : <FormattedMessage id="AUTH.RESET_PASS.SUBMIT" />}
        </button>
      </form>
    </div>;
};

export { ResetPasswordChange };